import React, { useCallback, useEffect } from 'react';
import {
    ActionIcon,
    Button,
    Flex,
    Highlight,
    Paper,
    ScrollArea,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
    Tooltip,
} from '@mantine/core';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { IconArrowRight, IconDownload, IconMail, IconPlus, IconSearch, IconTrash } from '@tabler/icons-react';
import { theme } from '../../../global/style/theme.tsx';
import { ILifeEvent } from '../../../global/entities/lifeEvents/LifeEvent.ts';
import { useGetLifeEvents } from '../../../global/entities/lifeEvents/queries/useGetLifeEvents.tsx';
import { TableContextProvider, Th, useTable } from '../../../global/hooks/useTable.tsx';
import dayjs from 'dayjs';
import { useElementSize } from '@mantine/hooks';
import { containsStringIgnoreCase } from '../../HcpDashboard/Table/PatientTable.tsx';
import { modals } from '@mantine/modals';
import { useDeleteLifeEventMutations } from '../../../global/entities/lifeEvents/mutations/useDeleteLifeEventMutations.tsx';
import { useDownloadLifeEventsPdf } from '../../../global/entities/lifeEvents/mutations/useDownloadLifeEventsPdf.tsx';
import { useSendLifeEventsToAssistant } from '../../../global/entities/lifeEvents/mutations/useSendLifeEventsToAssistant.tsx';
import Utils from '../../../global/helpers/utils.ts';
import { useNavigate } from 'react-router-dom';
import { PatientStore } from '../../../global/Store/PatientStore.ts';
import { useSessionProvider } from '../../../global/provider/sessionContext.tsx';
import { RouteStore } from '../../../global/Store/RouteStore.ts';

export type ILifeEventsTableProps = {
    patientStore?: PatientStore;
    routeStore?: RouteStore;
};

const LifeEventsTable: React.FC<ILifeEventsTableProps> = inject(
    'patientStore',
    'routeStore',
)(
    observer(({ patientStore, routeStore }) => {
        const { t, i18n } = useTranslation();
        const navigate = useNavigate();
        const { currentUser } = useSessionProvider();

        const patientId = patientStore?.patientId || currentUser?.patientID;
        const isHCP = !!currentUser?.healthcareProfessionalID;

        const { data: lifeEvents } = useGetLifeEvents(patientId);
        const deleteLifeEvent = useDeleteLifeEventMutations();
        const downloadPdfMutation = useDownloadLifeEventsPdf();
        const sendToAssistantMutation = useSendLifeEventsToAssistant();

        const handleDownloadPdf = () => {
            if (!patientId) return;
            downloadPdfMutation.mutate({
                patientId: String(patientId),
                language: i18n.language,
                patientName: patientStore?.name,
                patientDateOfBirth: patientStore?.birthDate,
            });
        };

        const handleSendToAssistant = () => {
            if (!patientId) return;
            sendToAssistantMutation.mutate({
                patientId: String(patientId),
                language: i18n.language,
            });
        };

        const { setSortedData, sortedData, sortBy, reverseSortDirection, setSorting, search, setSearch } = useTable();
        const { ref, width } = useElementSize();

        useEffect(() => {
            setSortedData([]);
            if (lifeEvents?.length) 
                setSortedData(lifeEvents);
            
        }, [lifeEvents, setSortedData]);

        useEffect(() => {
            routeStore?.setFromLocation();
        }, []);

        const openModalDelete = (id: string) => {
            const patientId = patientStore?.patientId || currentUser?.patientID;
            if (!patientId) 
                return;
            
            return modals.openConfirmModal({
                title: t('modal.deleteItemModal.title'),
                children: (
                    <Stack gap={'sm'}>
                        <Text>{t('modal.deleteItemModal.question')}</Text>
                        <Text fw={'bold'}> {t('modal.deleteItemModal.details')}</Text>
                    </Stack>
                ),
                labels: { confirm: t('button.delete'), cancel: t('button.cancel') },
                confirmProps: { color: 'red' },
                cancelProps: { variant: 'outline', color: 'gray' },
                onCancel: () => console.log('Suppression annulée'),
                onConfirm: () => onClickDelete(id, patientId),
                size: 'lg',
                radius: 'xs',
            });
        };

        const onClickDelete = useCallback(
            (taskId: string, patientId: string) => {
                if (!taskId || !patientId) 
                    return;
                
                deleteLifeEvent.mutateAsync({
                    id: taskId,
                    patientId: patientId,
                });
            },
            [deleteLifeEvent],
        );
        const handLelifeEvents = (id?: number) => {
            navigate(`../lifeEvents/${id}`);
        };

        return (
            <Paper ref={ref} h={'min-content'} w={'100%'}>
                <Stack h={'100%'}>
                    <Title order={4}>{location.pathname.includes('lifeEvents') === false ? t('table.lifeEvents.lifeEvents') : ''}</Title>
                    <Flex align={'center'} gap={'sm'}>
                        <TextInput
                            placeholder={t('global.search')}
                            rightSection={<IconSearch />}
                            value={search}
                            color={'var(--mantine-color-default-color)'}
                            style={{ flex: 1 }}
                            radius={'md'}
                            onChange={(el) => setSearch(el.currentTarget.value)}
                        />
                        <Button
                            variant="filled"
                            leftSection={<IconPlus size={14} />}
                            onClick={() => handLelifeEvents(0)}
                        >
                            {t('table.lifeEvents.addLifeEvent')}
                        </Button>
                    </Flex>
                    <ScrollArea w="100%">
                        <Table>
                            <Table.Thead>
                                <Table.Tr>
                                    <Th
                                        sorted={sortBy === 'date'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('date', sortedData)}
                                    >
                                        <Stack gap={0} ta={'start'}>
                                            {t('global.date')}
                                            <Text size={'xs'} c={'dimmed'}>
                                                YYYY-MM-DD
                                            </Text>
                                        </Stack>
                                    </Th>

                                    <Th
                                        sorted={sortBy === 'title'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('title', sortedData)}
                                    >
                                        {t('table.lifeEvents.eventTitle')}
                                    </Th>
                                    <Th
                                        sorted={sortBy === 'description'}
                                        reversed={reverseSortDirection}
                                        onSort={() => setSorting('description', sortedData)}
                                        visibleFrom={theme.breakpoints?.xs}
                                        width={400}
                                    >
                                        {t('formGlobal.description')}
                                    </Th>
                                    <Table.Th maw={50} />
                                </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                                {sortedData?.map((row: ILifeEvent, index) => {
                                    const isSearch =
                                        containsStringIgnoreCase(
                                            [
                                                `${dayjs(row.date).format('YYYY-MM-DD')}`,
                                                `${row.title}`,
                                                `${row.description}`,
                                            ],
                                            search,
                                        ) || !search;
                                    return (
                                        <Table.Tr
                                            key={index}
                                            style={{
                                                background:
                                                    index % 2 === 1
                                                        ? 'var(--Background-Gray-light, #E9ECEF)'
                                                        : undefined,
                                            }}
                                            hidden={!isSearch}
                                        >
                                            <Table.Td>
                                                <Highlight
                                                    highlight={search}
                                                    highlightStyles={{
                                                        color: 'var(--mantine-color-gray-9)',
                                                        fontWeight: 700,
                                                        WebkitBackgroundClip: 'text',
                                                    }}
                                                    styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                    truncate
                                                >
                                                    {Utils.formatDateToYYYYMMDD(row.date)?.toString() ||
                                                        new Date().toString()}
                                                </Highlight>
                                            </Table.Td>

                                            <Table.Td>
                                                <Highlight
                                                    highlight={search}
                                                    highlightStyles={{
                                                        color: 'var(--mantine-color-gray-9)',
                                                        fontWeight: 700,
                                                        WebkitBackgroundClip: 'text',
                                                    }}
                                                    styles={{ root: { fontSize: 14, fontWeight: 500 } }}
                                                    truncate
                                                >
                                                    {row.title}
                                                </Highlight>
                                            </Table.Td>
                                            <Table.Td visibleFrom={theme.breakpoints?.md} maw={400}>
                                                <Tooltip
                                                    label={row.description}
                                                    multiline
                                                    w={400}
                                                    withArrow
                                                    disabled={!row.description}
                                                >
                                                    <Highlight
                                                        highlight={search}
                                                        highlightStyles={{
                                                            color: 'var(--mantine-color-gray-9)',
                                                            fontWeight: 700,
                                                            WebkitBackgroundClip: 'text',
                                                        }}
                                                        styles={{
                                                            root: {
                                                                fontSize: 14,
                                                                fontWeight: 500,
                                                                whiteSpace: 'normal',
                                                                wordBreak: 'break-word',
                                                                display: '-webkit-box',
                                                                WebkitLineClamp: 4,
                                                                WebkitBoxOrient: 'vertical',
                                                                overflow: 'hidden',
                                                            },
                                                        }}
                                                    >
                                                        {row.description}
                                                    </Highlight>
                                                </Tooltip>
                                            </Table.Td>
                                            <Table.Td>
                                                <Flex align={'center'}>
                                                    <Tooltip label={t('toolTip.delete')}>
                                                        <ActionIcon
                                                            variant="subtle"
                                                            color="red"
                                                            onClick={() => openModalDelete(row.id!.toString())}
                                                        >
                                                            <IconTrash size={18} />
                                                        </ActionIcon>
                                                    </Tooltip>

                                                    <ActionIcon
                                                        variant={'transparent'}
                                                        onClick={() => handLelifeEvents(row.id)}
                                                    >
                                                        <IconArrowRight color={theme.colors?.blue?.toString()} />
                                                    </ActionIcon>
                                                </Flex>
                                            </Table.Td>
                                        </Table.Tr>
                                    );
                                })}
                            </Table.Tbody>
                        </Table>
                    </ScrollArea>
                    {isHCP && (lifeEvents?.length ?? 0) > 0 && (
                        <Flex justify="flex-end" gap="sm" wrap="wrap" mt="md">
                            <Button
                                variant="light"
                                leftSection={<IconDownload size={16} />}
                                onClick={handleDownloadPdf}
                                loading={downloadPdfMutation.isPending}
                            >
                                {t('lifeEventsHistory.actions.downloadPdf')}
                            </Button>
                            <Button
                                variant="light"
                                color="grape"
                                leftSection={<IconMail size={16} />}
                                onClick={handleSendToAssistant}
                                loading={sendToAssistantMutation.isPending}
                            >
                                {t('lifeEventsHistory.actions.sendToAssistant')}
                            </Button>
                        </Flex>
                    )}
                </Stack>
            </Paper>
        );
    }),
);

const WrappedLifeEventsTable: React.FC = () => {
    return (
        <TableContextProvider>
            <LifeEventsTable />
        </TableContextProvider>
    );
};

export default WrappedLifeEventsTable;
